import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import arrow from "./photos/arrow.svg";
import settings from "./photos/settings.svg";
import { entryRender } from "./Components/entryRender.tsx";

interface AIRoomGameProps {
  roomName: string;
  setAIRoomState: (state: string) => void;
  socket: any;
  containerHeight: number;
  fetchString: string;
  isOwner: boolean;
  onOpenSettings: () => void;
}

function AIRoomGame({
  roomName,
  setAIRoomState,
  socket,
  containerHeight,
  fetchString,
  isOwner,
  onOpenSettings,
}: AIRoomGameProps) {
  const { t } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState<number | null>(null);
  const [prompts, setPrompts] = useState<string[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);

  const topBarRender = (): JSX.Element => (
    <div className="container-row lift-topbar">
      <div className="container-row-start">
        <div
          className="button-tertiary-m button-text-m text-color-black"
          onClick={() => setAIRoomState("lobby")}
          style={{ maxHeight: "32.5px" }}
        >
          {t("back")}
        </div>
      </div>
      <div
        className="container-row"
        onClick={() => (isOwner ? onOpenSettings() : setAIRoomState("lobby"))}
        style={{ alignItems: "center", cursor: "pointer" }}
      >
        <div
          className="text-h3 text-color-black left-margin-s text-force-break button-tertiary-m text-color-black"
          style={{ maxHeight: "32.5px" }}
        >
          {roomName}
        </div>
        {isOwner && (
          <img
            src={settings}
            alt="Settings"
            className="settings-icon"
            style={{
              marginBottom: "-7px",
              marginLeft: "calc(var(--spacing-m) - 4px)",
            }}
          />
        )}
      </div>
    </div>
  );

  function prevButtonRender() {
    if (currentIndex > 0) {
      return (
        <div
          className="button-next-prev button-prev text-color-black shadow-black"
          onClick={prev}
        >
          <img className="button-prev-img" src={arrow} alt="" />
        </div>
      );
    } else {
      return (
        <div
          className="button-next-prev button-prev text-color-black shadow-black box-button-center"
          onClick={prev}
          style={{ visibility: "hidden" }}
        >
          <img className="button-prev-img" src={arrow} alt="" />
        </div>
      );
    }
  }

  function nextButtonRender() {
    // if (currentIndex < prompts.length - 1) {
    return (
      <div
        className="button-next-prev text-color-black shadow-black box-button-center next-button"
        onClick={next}
      >
        {t("next")}
      </div>
    );
    // }
  }

  const prev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const fetchPrompts = useCallback(async (): Promise<any> => {
    try {
      const res = await fetch(`${fetchString}${roomName}`, {
        headers: { Accept: "application/json" },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch prompts: ${res.status}`);
      }

      const data = await res.json();
      return data;
    } catch (error) {
      console.error("Error fetching prompts:", error);
      throw error;
    }
  }, [fetchString, roomName]);

  const next = async () => {
    if (!isAnimating) {
      if (currentIndex === prompts.length - 1) {
        // Need to fetch more prompts
        const roomData = await fetchPrompts();
        const promptsData = roomData.data?.prompts ?? [];
        const fetchedPrompts = promptsData.map(
          (prompt: { value: string }) => prompt.value,
        );
        if (fetchedPrompts.length <= prompts.length) {
          // No new prompts yet
          console.log("No new prompts yet, please try again later.");
          return;
        }

        const newPrompts = fetchedPrompts.slice(prompts.length);
        setPrompts((prevPrompts) => [...prevPrompts, ...newPrompts]);
      }
      socket.emit("ai room current index", {
        roomName,
        currentIndex: currentIndex + 1,
      });

      setIsAnimating(true);
      setPreviousIndex(currentIndex);
      setCurrentIndex(currentIndex + 1);

      setTimeout(() => {
        setPreviousIndex(null);
        setIsAnimating(false);
      }, 700); // Adjust this value based on your desired animation duration
    }
  };

  // Fetch data on mount
  useEffect(() => {
    fetchPrompts().then((roomData) => {
      const promptsData = roomData.data?.prompts ?? [];
      const fetchedPrompts = promptsData.map(
        (prompt: { value: string }) => prompt.value,
      );
      setPrompts(fetchedPrompts);
      const highestIdx = roomData.data?.currentIndex ?? 0;
      setCurrentIndex(highestIdx);
    });
  }, [fetchPrompts, socket]);

  const divStyle = {
    top: -containerHeight / 10,
  };

  return (
    <div className="App-header">
      {topBarRender()}
      {prompts.length > 0 &&
        entryRender(divStyle, previousIndex, currentIndex, prompts, isAnimating)}

      <div className="prev-next-buttons gap-20">
        {prompts.length > 0 && (
          <>
            {prevButtonRender()}
            {nextButtonRender()}
          </>
        )}
      </div>
    </div>
  );
}

export default AIRoomGame;
